import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, ScrollView, ActivityIndicator, RefreshControl } from 'react-native';
import { observer } from 'mobx-react';

import Text from '../components/shared/Text';
import Button from '../components/shared/Button';
import IconButton from '../components/shared/IconButton';
import { InfoIcon } from '../components/common/Info';
import warriorInfoModalStore from '../components/common/WarriorInfoModal/store';

import { fetchArenaLeaders, ArenaLeader } from '../lib/leaderboardCache';
import { findArenaMatch } from '../lib/apiCalls';
import { IS_WEB } from '../lib/appConstants';

import appStore from '../stores/app';
import heroStore from '../stores/hero';

import { getSeasonId, availableTickets } from '../utils/arenaUtils';
import { inCombat } from '../utils/warriorUtils';
import { getBuilding } from '../utils/buildingUtils';
import { getLocalValue, t } from '../utils/appUtils';

import { BG_OFFSET, PaperBackground, MAP_DIMENSIONS } from '../components/Island/IslandScreen';
import { BASE_COLOR, MARGIN_5, PADDING, WINDOW_WIDTH, pOrT } from '../components/styleConstants';

type Props = {
  id: string;
};

const Cell: React.FC<{ flex: number; bold?: boolean; children?: any }> = ({
  flex,
  bold,
  children,
}) => (
  <View style={{ flex, flexDirection: 'row', alignItems: 'center', paddingHorizontal: 4 }}>
    {typeof children === 'string' || typeof children === 'number' ? (
      <Text bold={bold}>{String(children)}</Text>
    ) : (
      children
    )}
  </View>
);

const Row: React.FC<{ header?: boolean; children?: any }> = ({ header, children }) => (
  <View
    style={{
      flexDirection: 'row',
      paddingVertical: 4,
      borderBottomWidth: header ? 1 : 0,
      borderBottomColor: '#8f6b50',
      marginBottom: header ? MARGIN_5 : 0,
    }}
  >
    {children}
  </View>
);

const Arena = observer(({ id }: Props) => {
  const building = getBuilding(appStore.initData.buildings, id)!;

  const [leaders, setLeaders] = useState<ArenaLeader[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [matching, setMatching] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await fetchArenaLeaders();
      setLeaders(data);
    } catch (e) {
      // leave prior standings in place on a failed refresh
    }
  }, []);

  // Standings don't refetch on their own after a fight (you return to the
  // island, not this screen), so offer an explicit refresh: pull-to-refresh on
  // native, a refresh button on web.
  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await load();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  // This screen stays mounted while a fight runs (you return to the island, not
  // here), so reload the standings when the combat clears — i.e. the fight just
  // finished — so the just-played result shows without a manual refresh.
  const combat = heroStore.hero ? heroStore.hero.combat : null;
  const prevCombat = useRef(combat);
  useEffect(() => {
    if (prevCombat.current && !combat) load();
    prevCombat.current = combat;
  }, [combat, load]);

  const arena = heroStore.hero && heroStore.hero.arena;
  // The backend re-applies the same refill authoritatively in findArenaMatch,
  // so this only re-enables the button — it can never grant a real ticket.
  const tickets = availableTickets(arena, Date.now());

  // Pull-to-refresh on native; web uses the header refresh button instead.
  const refreshControl = IS_WEB ? undefined : (
    <RefreshControl tintColor={BASE_COLOR} refreshing={refreshing} onRefresh={onRefresh} />
  );

  async function onFindMatch() {
    if (matching || inCombat(heroStore.hero)) return;
    setMatching(true);
    appStore.toggleLoading(true);
    try {
      // On success the backend sets hero.combat; the InnerContainer combat
      // observer navigates to the fight (same path as being attacked).
      await findArenaMatch();
    } catch (e: any) {
      appStore.toggleLoading(false);
      appStore.showMessage({ type: 'error', text: e?.message || String(e) });
    } finally {
      setMatching(false);
    }
  }

  return (
    <View>
      <PaperBackground width={MAP_DIMENSIONS.width} height={MAP_DIMENSIONS.height} />
      <View
        style={{
          marginLeft:
            MAP_DIMENSIONS.width + 15 > WINDOW_WIDTH
              ? BG_OFFSET / 2
              : (WINDOW_WIDTH - MAP_DIMENSIONS.width + 15) / 2,
          marginTop: BG_OFFSET / 2,
          width: MAP_DIMENSIONS.width - BG_OFFSET,
          height: MAP_DIMENSIONS.height - BG_OFFSET,
          padding: PADDING,
        }}
      >
        <Text bold style={{ fontSize: pOrT(14, 18), marginBottom: MARGIN_5 }}>
          {getLocalValue(building)}
        </Text>

        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: MARGIN_5 }}>
          <Text>{t('arena.season', { season: getSeasonId(Date.now()) })}</Text>
          <Text style={{ marginLeft: PADDING }}>{t('arena.tickets', { count: tickets })}</Text>
          <Button
            style={{ marginLeft: PADDING }}
            onPress={onFindMatch}
            disabled={matching || tickets < 1}
          >
            {t('arena.findMatch')}
          </Button>
          {IS_WEB ? (
            <IconButton
              onPress={onRefresh}
              style={{ marginLeft: PADDING }}
              size={12}
              name="refresh"
              title={t('refresh')}
              source={require('../assets/images/refresh.png')}
              iconStyle={{ width: 20, height: 20 }}
            />
          ) : null}
        </View>

        {loading || !leaders ? (
          <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
            <ActivityIndicator color={BASE_COLOR} />
          </View>
        ) : !leaders.length ? (
          <ScrollView style={{ flex: 1 }} refreshControl={refreshControl}>
            <Text style={{ marginTop: MARGIN_5 }}>{t('arena.empty')}</Text>
          </ScrollView>
        ) : (
          <ScrollView style={{ flex: 1 }} refreshControl={refreshControl}>
            <Row header>
              <Cell flex={0.2} bold>
                {t('arena.columns.rank')}
              </Cell>
              <Cell flex={2} bold>
                {t('arena.columns.login')}
              </Cell>
              <Cell flex={1.2} bold>
                {t('arena.columns.rating')}
              </Cell>
              <Cell flex={0.8} bold>
                {t('arena.columns.wins')}
              </Cell>
              <Cell flex={0.8} bold>
                {t('arena.columns.losses')}
              </Cell>
            </Row>
            {leaders.map((l) => (
              <Row key={l.id}>
                <Cell flex={0.2}>{l.rank}</Cell>
                <Cell flex={2}>
                  <Text bold>{l.login}</Text>
                  <InfoIcon
                    style={{ marginLeft: 5 }}
                    onPress={() => warriorInfoModalStore.toggle(l.id, true)}
                  />
                </Cell>
                <Cell flex={1.2}>{l.rating}</Cell>
                <Cell flex={0.8}>{l.wins}</Cell>
                <Cell flex={0.8}>{l.losses}</Cell>
              </Row>
            ))}
          </ScrollView>
        )}
      </View>
    </View>
  );
});

(Arena as any).displayName = 'Arena';

export default Arena;
